/**
 * Unified run state + live activity for each live run in the tab. One headless probe per live
 * run reads its daemon-published materialized state (batched snapshot read + live pushes; never
 * the event history) and reports `{phase, label, line, evidence}` upward; the lanes model
 * prefers it over the hub's run summary. Opening the run later seeds from the same state.
 */
import { liveActivity, type TranscriptSync, useNow, useRunState } from '@viko/transcript-ui'
import { useCallback, useEffect, useRef, useState } from 'react'
import type { LivePhase } from '../../lib/lanes-model.js'
import { useRunMaterial, viewAsks } from '../../lib/session.js'
import type { RunSummary } from '../../lib/types.js'

type Report = (runId: string, p: LivePhase | null) => void

/** Newest verified test count as a short line ("38 tests passing so far"). */
export function evidenceText(
  latest: readonly {
    kind: string
    status: string
    passed?: number
    failed?: number
    total?: number
  }[],
  busy: boolean,
): string | undefined {
  const t = latest.findLast((c) => c.kind === 'test')
  if (!t) return undefined
  if (t.status === 'fail' || t.status === 'error')
    return t.failed != null ? `${t.failed} tests failing` : 'Tests failing'
  if (t.passed == null) return busy ? 'Tests passing so far' : 'Tests passed'
  return busy ? `${t.passed} tests passing so far` : `${t.passed} tests passed`
}

function Probe({ run, report }: { run: RunSummary; report: Report }) {
  const view = useRunMaterial(run.runId)
  // The snapshot is exact at its seq; the daemon republishes on every change.
  const sync: TranscriptSync = { syncing: false }
  const rs = useRunState(view, {
    sync,
    lifecycle: {
      ended: run.status === 'done',
      ...(run.landing?.state ? { landing: run.landing.state } : {}),
    },
  })
  const busy = rs.phase === 'working' || rs.phase === 'making-progress'
  const now = useNow(busy, 5000)
  const asks = viewAsks(view)
  const act = liveActivity(view.activity, {
    now,
    asks,
    offline: view.connection === 'offline',
    idleText: rs.label,
  })
  const phase: LivePhase = {
    phase: rs.phase,
    label: rs.label,
    reason: rs.reason,
    line: act.now.text,
    streaming: act.now.kind === 'tool' || act.now.kind === 'thinking' || act.now.kind === 'writing',
    ...(() => {
      const e = evidenceText(rs.progress.latest, busy)
      return e ? { evidence: e } : {}
    })(),
    // auto-artifacts: posted artifacts show as a chip on the lane card
    ...(view.progress.artifacts.length ? { artifacts: view.progress.artifacts.length } : {}),
  }
  // No state yet (no snapshot): let the hub summary speak instead of "Not started".
  const known = view.lastSeq > 0
  const sig = known ? JSON.stringify(phase) : ''
  const last = useRef('')
  useEffect(() => {
    if (sig === last.current) return
    last.current = sig
    report(run.runId, known ? (JSON.parse(sig) as LivePhase) : null)
  }, [sig, known, report, run.runId])
  useEffect(() => () => report(run.runId, null), [report, run.runId])
  return null
}

/** Live phases for `runs`, keyed by runId. Render `probes` somewhere in the tree. */
export function useLivePhases(runs: readonly RunSummary[]) {
  const [phases, setPhases] = useState<ReadonlyMap<string, LivePhase>>(new Map())
  const report = useCallback<Report>((runId, p) => {
    setPhases((prev) => {
      if (!p && !prev.has(runId)) return prev
      const next = new Map(prev)
      if (p) next.set(runId, p)
      else next.delete(runId)
      return next
    })
  }, [])
  const probes = runs.map((r) => <Probe key={r.runId} run={r} report={report} />)
  return { phases, probes }
}
